import { createRouter, createWebHistory } from 'vue-router'
import { $t } from '@previo/core'
import { Routes } from './routes'
import { Layouts } from '~/layouts'
import { Environment } from '~/Environment'
import saveProjectAndReturnPath from '~/router/midleware/saveProjectAndReturnPath'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/entry-point/:project',
      meta: { layout: Layouts.NO_AUTH },
      beforeEnter: [saveProjectAndReturnPath],
      children: [
        {
          path: '',
          name: Routes.ENTRY_POINT_INDEX,
          meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Entry point')) },
          component: () => import('~/pages/entry-point/Index.vue'),
        },
        {
          path: 'logout',
          name: Routes.ENTRY_POINT_LOGOUT,
          meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Logout')) },
          component: () => import('~/pages/entry-point/Logout.vue'),
        },
      ],
    },

    {
      path: '/login',
      name: Routes.LOGIN_INDEX,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Login')) },
      component: () => import('~/pages/login/index/Index.vue'),
    },

    {
      path: '/grace-login',
      name: Routes.LOGIN_GRACE_LOGIN,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Grace login')) },
      component: () => import('~/pages/login/grace/Index.vue'),
    },

    {
      path: '/login/backup',
      name: Routes.BACKUP_INDEX,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Backup codes')) },
      component: () => import('~/pages/login/two-factor/Backup.vue'),
    },

    {
      path: '/login/two-factor/code/:type',
      name: Routes.LOGIN_TWO_FACTOR_CODE,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Two-factor authentication')) },
      component: () => import('~/pages/login/two-factor/code/Index.vue'),
    },

    {
      path: '/login/provider/:provider',
      name: Routes.LOGIN_PROVIDER,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Login with provider')) },
      component: () => import('~/pages/login/provider/Index.vue'),
    },

    {
      path: '/login/email/link-send',
      name: Routes.LOGIN_TWO_FACTOR_EMAIL_LINK_SEND,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Email link sent')) },
      component: () => import('~/pages/login/two-factor/email/EmailLinkSend.vue'),
    },

    {
      path: '/login/email/send-failed',
      name: Routes.LOGIN_TWO_FACTOR_EMAIL_SEND_FAILED,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('User verification')) },
      component: () => import('~/pages/login/two-factor/email/EmailSendFailed.vue'),
    },

    {
      path: '/trust-device/:verification',
      name: Routes.LOGIN_TWO_FACTOR_EMAIL,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Trust device')) },
      component: () => import('~/pages/login/two-factor/email/EmailConfirm.vue'),
    },

    {
      path: '/two-factor/:type(otp)/setup/:verification',
      name: Routes.TWO_FA_INDEX,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Two-factor setup')) },
      component: () => import('~/pages/two-factor-setup/Index.vue'),
    },

    {
      path: '/project-login',
      name: Routes.PROJECT_LOGIN_INDEX,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Project login')) },
      component: () => import('~/pages/project-login/Index.vue'),
    },

    {
      path: '/change-password/forgot',
      name: Routes.RESET_PASSWORD_FORGOT,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Forgot password')) },
      component: () => import('~/pages/reset-password/Forgot.vue'),
    },

    {
      path: '/change-password/:token',
      name: Routes.RESET_PASSWORD_INDEX,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Reset password')) },
      component: () => import('~/pages/reset-password/Index.vue'),
    },

    {
      path: '/invitation/accept/:token',
      name: Routes.INVITATION_ACCEPT,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Accept invitation')) },
      component: () => import('~/pages/invitation/Index.vue'),
    },

    {
      path: '/expired-link',
      name: Routes.EXPIRED_LINK,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Expired link')) },
      component: () => import('~/pages/expired-link/Index.vue'),
    },

    {
      path: '/verify-action',
      name: Routes.VERIFY_ACTION_INDEX,
      meta: { layout: Layouts.AUTH_BLANK, title: () => pageTitle($t('Verify action')) },
      component: () => import('~/pages/verify-action/Index.vue'),
    },

    {
      path: '/builder',
      name: Routes.BUILDER_INDEX,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Builder signpost')) },
      beforeEnter: () => (Environment.isEnvLikeDevelopment() ? true : { name: Routes.LOGIN_INDEX }),
      component: () => import('~/pages/builder/Index.vue'),
    },

    {
      path: '/:pathMatch(.*)*',
      redirect: '/login',
    },
  ],
})

const pageTitle = (moduleTitle: string): string => {
  return `Previo ID - ${moduleTitle}`
}

router.afterEach((to) => {
  if (to.meta.title) {
    document.title = to.meta.title()
  }
})

export default router

// Each RouteMeta contain layout
declare module 'vue-router' {
  interface RouteMeta {
    layout: Layouts
    title?: () => string
  }
}
